// Minimal REST helpers for the dashboard backend (loco). Replaces the
// retired JSON-RPC envelope (`client.ts`): endpoints are plain
// `GET/POST/PUT /api/...` returning bare JSON bodies. Backend `Decimal`
// values arrive as JSON strings — render them verbatim, never parseFloat
// before display.
//
// Every request carries `Authorization: Bearer <jwt>` when a session
// token is present; a 401 clears the token (which logs the user out and
// redirects to /login via the auth layer).

import { authHeader, clearToken } from '../auth/session';

export class ApiError extends Error {
  /** HTTP status code (503 ⇒ subsystem not configured, 404 ⇒ not found). */
  readonly status: number;
  /** Backend error code, e.g. `not_configured` (loco ErrorDetail). */
  readonly code?: string;

  constructor(status: number, message: string, code?: string) {
    super(`${status}: ${message}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function toApiError(res: Response): Promise<ApiError> {
  let message = res.statusText || `HTTP ${res.status}`;
  let code: string | undefined;
  try {
    const body = (await res.json()) as {
      error?: string;
      description?: string;
      reason?: string;
    };
    // loco ErrorDetail: { error: "<code>", description: "<human text>" }
    if (body.description) {
      message = body.description;
      code = body.error;
    } else if (body.reason) {
      // quant control server rejections: { applied: false, reason }
      // (respec / lifecycle endpoints, proxied through /api/grid).
      message = body.reason;
    } else if (body.error) {
      message = body.error;
    }
  } catch {
    // non-JSON body — keep the status text
  }
  return new ApiError(res.status, message, code);
}

/// Clear the session on 401 so the auth layer redirects to /login. The
/// login request itself never has a token, so this is a no-op there.
function handleUnauthorized(status: number): void {
  if (status === 401) clearToken();
}

export async function getJson<T = unknown>(
  path: string,
  signal?: AbortSignal,
): Promise<T> {
  const res = await fetch(path, { signal, headers: { ...authHeader() } });
  if (!res.ok) {
    handleUnauthorized(res.status);
    throw await toApiError(res);
  }
  return (await res.json()) as T;
}

export async function sendJson<T = unknown>(
  method: 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: { 'content-type': 'application/json', ...authHeader() },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    handleUnauthorized(res.status);
    throw await toApiError(res);
  }
  return (await res.json()) as T;
}
